import clsx from 'clsx';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import dropdownBlack from '../../../../../../assets/dropdown-black.svg';
import infoBlack from '../../../../../../assets/info.svg';
import { caretRightPurple } from '../../../../../../assets/hostedassets';
import LoadingSpinner from '../../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import Tooltip from '../../../../../../common/_custom/Tooltip/Tooltip';
import { useLatestRequestGuard } from '../../../../../../common/hooks/useLatestRequestGuard';
import { fetchBankStatementData } from '../../../../../../store/dashboard/bankStatement/apiHelper';
import {
    fetchBankEntityRationaleService,
    fetchBankStatementCashflowBucketsService,
    fetchBankTransactionsByBucketService,
    BucketPayload,
} from '../../../../../../store/dashboard/financials/entityRationaleServices';
import { JAVA_API_STATUS } from '../../../../../../utils/axios-interceptor';
import { capitalizeWords } from '../../../../../../utils/utils';
import EmptyBank from '../../../BankDataTable/components/EmptyBank/EmptyBank';
import { chipInfoForEntity, getSourceChipInfo, isNoiseRationale } from './rationaleChips';
import {
    amountsToMap,
    buildCashflowAggregates,
    buildCashflowLayout,
    CashflowAggregates,
    formatTableAmount,
    GroupRow,
    rowTotal,
} from './cashflowLayout';
import { EntityRationale, BankTransaction, ChipInfo, CashflowSubRow } from './types';
import EntityDrillDownDrawer from './EntityDrillDownDrawer';
import styles from './FinancialsFromBankingV2.module.scss';

const EMPTY_AGGREGATES: CashflowAggregates = { opening: {}, netCashflow: {}, closing: {} };

// Special rows whose label carries a design colour cue.
const SPECIAL_LABEL_CLASS: Partial<Record<GroupRow['kind'], string>> = {
    intracompany: styles.IntracompanyLabel,
    unclassified: styles.UnclassifiedLabel,
};

/** Display-only title-casing for leaf entity names. */
const toDisplayName = (name: string): string => capitalizeWords(name) ?? name;

/** Sort leaf sub-rows: 'other' residual last; all others by |total| descending, then name ascending. */
function sortedSubRows(subRows: CashflowSubRow[]): CashflowSubRow[] {
    return [...subRows].sort((a, b) => {
        const aIsOther = a.bucketType === 'other';
        const bIsOther = b.bucketType === 'other';
        if (aIsOther !== bIsOther) return aIsOther ? 1 : -1;
        const diff = Math.abs(rowTotal(b.amountsMap)) - Math.abs(rowTotal(a.amountsMap));
        if (diff !== 0) return diff;
        return (a.name ?? '').toLowerCase().localeCompare((b.name ?? '').toLowerCase());
    });
}

interface DrawerState {
    entityName: string;
    transactions: BankTransaction[];
    loading: boolean;
}

function renderChips(chipInfo: ChipInfo | null) {
    if (!chipInfo) return null;
    const tooltipContent = (
        <div className={styles.ChipTooltip}>
            <div>{chipInfo.hoverPayload.entityRationale}</div>
            {chipInfo.hoverPayload.primaryDocName && <div className={styles.ChipTooltipDoc}>{chipInfo.hoverPayload.primaryDocName}</div>}
            {chipInfo.hoverPayload.allCitations.length > 1 && (
                <div className={styles.ChipTooltipMore}>
                    {chipInfo.hoverPayload.allCitations.slice(1).map((c, i) => (
                        <div key={i}>{[c.doc_type, c.doc_name].filter(Boolean).join(' — ')}</div>
                    ))}
                </div>
            )}
        </div>
    );
    return (
        <span className={styles.ChipGroup}>
            <Tooltip content={tooltipContent} direction="top" usePortal fitContent>
                <span className={styles.Chip}>
                    <span className={styles.ChipLabel}>{chipInfo.label}</span>
                    <img src={infoBlack} alt="" className={styles.ChipInfoIcon} />
                </span>
            </Tooltip>
            {chipInfo.isLender && (
                <Tooltip content="Name match from Known Lender List" direction="top" usePortal fitContent>
                    <span className={styles.Chip}>
                        KNOWN LENDER
                        <img src={infoBlack} alt="" className={styles.ChipInfoIcon} />
                    </span>
                </Tooltip>
            )}
        </span>
    );
}

interface FinancialsFromBankingV2Props {
    /** Applied Banking-tab Time Period window ('' when unset); scopes both the table and drawers. */
    startDate?: string;
    endDate?: string;
    /** Reports the org's full bank_transactions availability (span.minDate/maxDate,
     *  'YYYY-MM-DD') so the Banking tab widens its date-picker envelope to it. */
    onDataSpan?: (minDate: string, maxDate: string) => void;
    /** Reports the API-applied default window (startDate/endDate, 'YYYY-MM-DD') so the
     *  CFB time-period pickers can display it when no explicit selection exists. */
    onAppliedWindow?: (startDate: string, endDate: string) => void;
    /** Divisor to apply to all table amount cells (1 = raw ₹, 1e5 = Lakhs, 1e7 = Crore). Drawer unaffected. */
    unitDivisor?: number;
}

function FinancialsFromBankingV2({
    startDate = '',
    endDate = '',
    onDataSpan,
    onAppliedWindow,
    unitDivisor = 1,
}: FinancialsFromBankingV2Props) {
    const [tableData, setTableData] = useState<any>();
    const [aggregates, setAggregates] = useState<CashflowAggregates>(EMPTY_AGGREGATES);
    const [fetchingData, setFetchingData] = useState(false);
    const [rationaleEntities, setRationaleEntities] = useState<EntityRationale[]>([]);
    const [drawer, setDrawer] = useState<DrawerState | null>(null);
    const [effectiveWindow, setEffectiveWindow] = useState<{ startDate: string; endDate: string } | null>(null);
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    // Per-group visible leaf count; absent = default page size. Each "Load more" click adds one page.
    const [visibleCountByGroup, setVisibleCountByGroup] = useState<Map<string, number>>(new Map());

    const LEAF_PAGE_SIZE = 30;
    const params = useParams();
    const orgId = params.id as string;
    const { startRequest, isCurrent } = useLatestRequestGuard();
    const { startRequest: startAggregatesRequest, isCurrent: isAggregatesCurrent } = useLatestRequestGuard();

    const noData = !tableData?.headers?.length;

    const periodHeaders = tableData?.headers ?? [];
    const layout = useMemo(() => buildCashflowLayout(tableData?.rows ?? []), [tableData?.rows]);
    const totalColumnCount = periodHeaders.length + 3; // name col + Tagging Rational col + period cols + Total col

    // Tier-2 chip lookup keyed by entityId — built from the raw per-entity array so
    // entities sharing one rationale string (clobbered in rationaleMap) all get chips.
    const entityChipMap = useMemo<Map<number, ChipInfo>>(() => {
        const result = new Map<number, ChipInfo>();
        for (const entity of rationaleEntities) {
            const info = chipInfoForEntity(entity);
            if (info) result.set(entity.entityId, info);
        }
        return result;
    }, [rationaleEntities]);

    // Fallback rationale label keyed by `${entityId}:${classificationId}` — scoped to the
    // row's own classification so an entity's intra rationale never bleeds onto its operating row.
    // Built from the RAW response array (not rationaleMap) so shared-rationale entities aren't clobbered.
    // Tie-break: response order, first non-noise non-null rationale per (entityId, classificationId) wins.
    // If classificationId is absent on ANY item (pre-contract backend), the map is left empty and
    // kind2RationaleReady is false — disables the kind-2 rung to avoid cross-classification borrowing.
    const { entityRationaleLabel, kind2RationaleReady } = useMemo<{
        entityRationaleLabel: Map<string, string>;
        kind2RationaleReady: boolean;
    }>(() => {
        const result = new Map<string, string>();
        if (rationaleEntities.some((e) => e.classificationId === undefined)) {
            return { entityRationaleLabel: result, kind2RationaleReady: false };
        }
        for (const entity of rationaleEntities) {
            const key = `${entity.entityId}:${entity.classificationId}`;
            if (!result.has(key) && entity.rationale && !isNoiseRationale(entity.rationale)) {
                result.set(key, entity.rationale);
            }
        }
        return { entityRationaleLabel: result, kind2RationaleReady: true };
    }, [rationaleEntities]);

    // Table refetches on org change AND on the Banking tab's applied Time Period;
    // one effect so a deep-link mount fires a single buckets request.
    useEffect(() => {
        fetchTableData();
    }, [orgId, startDate, endDate]);

    useEffect(() => {
        fetchRationaleData();
    }, [orgId]);

    useEffect(() => {
        fetchAggregates();
    }, [orgId, effectiveWindow?.startDate, effectiveWindow?.endDate]);

    const fetchTableData = async (silent = false) => {
        if (!silent) setFetchingData(true);
        const requestToken = startRequest();
        try {
            const response = await fetchBankStatementCashflowBucketsService({ startDate, endDate });
            if (!isCurrent(requestToken)) return;
            const responseData = response.data?.responseData;
            if (responseData?.responseCode === JAVA_API_STATUS.SUCCESS) {
                const data = response.data?.data ?? {};
                const transformedRows = data?.rows?.map((row: any) => ({
                    ...row,
                    amountsMap: amountsToMap(row?.amounts),
                    subRows: row?.subRows?.map((subrow: any) => ({
                        ...subrow,
                        amountsMap: amountsToMap(subrow?.amounts),
                    })),
                }));
                setTableData({
                    headers: data?.headers ?? [],
                    rows: transformedRows,
                });
                // span always reflects full availability, independent of any window.
                if (data?.span?.minDate && data?.span?.maxDate) {
                    onDataSpan?.(data.span.minDate, data.span.maxDate);
                }
                // The server applies a last-18-months-of-data default when unwindowed;
                // drawers must use the same effective window for table↔drawer parity.
                const aw = data?.appliedWindow?.startDate && data?.appliedWindow?.endDate ? data.appliedWindow : null;
                setEffectiveWindow(aw ? { startDate: aw.startDate, endDate: aw.endDate } : null);
                if (aw) onAppliedWindow?.(aw.startDate, aw.endDate);
            }
            setFetchingData(false);
        } catch {
            if (isCurrent(requestToken)) setFetchingData(false);
        }
    };

    // The bucket response carries no Opening/Net Cashflow/Closing rows — pull them from the
    // account-summary source and sum across accounts. Pass the effective window so aggregates
    // stay in sync with the table period; no selectedBankId (unknown to BankStatementSummaryRequest).
    const fetchAggregates = async () => {
        const requestToken = startAggregatesRequest();
        await fetchBankStatementData(
            effectiveWindow ? { startDate: effectiveWindow.startDate, endDate: effectiveWindow.endDate } : {},
            {
                onSuccess: (bankData) => {
                    if (!isAggregatesCurrent(requestToken)) return;
                    setAggregates(buildCashflowAggregates(bankData?.summaryData ?? []));
                },
                onError: () => {
                    if (!isAggregatesCurrent(requestToken)) return;
                    setAggregates(EMPTY_AGGREGATES);
                },
            },
        );
    };

    const fetchRationaleData = async () => {
        try {
            const response = await fetchBankEntityRationaleService();
            const responseData = response.data?.responseData;
            if (responseData?.responseCode === JAVA_API_STATUS.SUCCESS) {
                const entities: EntityRationale[] = response.data?.data?.entities ?? [];
                setRationaleEntities(entities);
            }
        } catch {
            // Degrade gracefully — no chips, base table unaffected
        }
    };

    const openDrawer = async (displayName: string, payload: BucketPayload) => {
        setDrawer({ entityName: displayName, transactions: [], loading: true });
        try {
            const response = await fetchBankTransactionsByBucketService(payload);
            const responseData = response.data?.responseData;
            const rows: BankTransaction[] = responseData?.responseCode === JAVA_API_STATUS.SUCCESS ? response.data?.data ?? [] : [];
            setDrawer({ entityName: displayName, transactions: rows, loading: false });
        } catch {
            setDrawer({ entityName: displayName, transactions: [], loading: false });
        }
    };

    const toggleGroup = (groupKey: string) => {
        setExpandedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(groupKey)) next.delete(groupKey);
            else next.add(groupKey);
            return next;
        });
    };

    // Parse and scale a raw server amount value, then reformat for display.
    // Wire format: comma-formatted strings with leading '-' for negatives (e.g. '-40,000').
    // Parenthesized negatives never arrive from the API — they are a display-only convention.
    // Shared <colgroup> applied to every table in the multi-table stack so table-layout:fixed
    // keeps all columns aligned. Name=390px, Rationale=224px (14rem), period cols=210px each,
    // Total=150px — matches the SCSS cell min/max-width values exactly.
    const renderColGroup = () => (
        <colgroup>
            <col style={{ width: '390px' }} />
            <col style={{ width: '14rem' }} />
            {periodHeaders.map((h: string) => (
                <col key={h} style={{ width: '210px' }} />
            ))}
            <col style={{ width: '150px' }} />
        </colgroup>
    );

    const scaledFmt = (raw: any): string => {
        if (raw == null) return '-';
        const n = typeof raw === 'number' ? raw : parseFloat(String(raw).replace(/,/g, ''));
        return Number.isFinite(n) ? formatTableAmount(n / unitDivisor) : String(raw);
    };

    const periodCells = (amountsMap: Record<string, any> = {}) =>
        periodHeaders.map((header: string, j: number) => {
            const raw = amountsMap?.[header];
            const isNeg = raw != null && (typeof raw === 'number' ? raw < 0 : String(raw).trim().startsWith('-'));
            return (
                <td key={`${header}-${j}`} className={clsx(styles.TableBodyRowItem, { [styles.Negative]: isNeg })}>
                    {scaledFmt(raw)}
                </td>
            );
        });

    const totalCell = (amountsMap: Record<string, any> = {}) => {
        const total = rowTotal(amountsMap);
        return (
            <td className={clsx(styles.TableBodyRowItem, styles.TotalCell, { [styles.Negative]: total < 0 })}>
                {formatTableAmount(total / unitDivisor)}
            </td>
        );
    };

    const renderSectionBand = (band: string) => (
        <tr key={band} className={styles.SectionBandRow}>
            <td className={styles.SectionBandCell} colSpan={totalColumnCount}>
                <span className={styles.SectionBandLabel}>{band}</span>
            </td>
        </tr>
    );

    const renderNetRow = (label: string, amountsMap: Record<string, number>) => (
        <tr key={label} className={styles.NetRow}>
            <td className={clsx(styles.TableBodyRowItem, styles.NetCell)}>{label}</td>
            <td className={clsx(styles.TableBodyRowItem, styles.NetCell)} />
            {periodHeaders.map((header: string, j: number) => {
                const v = amountsMap?.[header];
                return (
                    <td
                        key={`${header}-${j}`}
                        className={clsx(styles.TableBodyRowItem, styles.NetCell, { [styles.Negative]: v != null && v < 0 })}
                    >
                        {v != null ? formatTableAmount(v / unitDivisor) : '-'}
                    </td>
                );
            })}
            <td className={clsx(styles.TableBodyRowItem, styles.NetCell, { [styles.Negative]: rowTotal(amountsMap) < 0 })}>
                {formatTableAmount(rowTotal(amountsMap) / unitDivisor)}
            </td>
        </tr>
    );

    const renderAggregateRow = (
        label: string,
        amountsMap: Record<string, number>,
        showFormula = false,
        isNet = false,
        totalMode: 'sum' | 'first' | 'last' = 'sum',
    ) => {
        const firstPeriod = periodHeaders[0];
        const lastPeriod = periodHeaders[periodHeaders.length - 1];
        const aggTotal =
            totalMode === 'first' ? (amountsMap?.[firstPeriod] ?? 0)
            : totalMode === 'last' ? (amountsMap?.[lastPeriod] ?? 0)
            : rowTotal(amountsMap);
        return (
            <tr key={label} className={clsx(styles.TableBodyRow, isNet ? styles.AggregateNetRow : styles.AggregateRow)}>
                <td className={clsx(styles.TableBodyRowItem, styles.AggregateCell)}>
                    {showFormula ? (
                        <Tooltip content={FORMULA_HOVER} direction="top" fitContent>
                            <span>{label}</span>
                        </Tooltip>
                    ) : (
                        label
                    )}
                </td>
                <td className={clsx(styles.TableBodyRowItem, styles.AggregateCell)} />
                {periodHeaders.map((header: string, j: number) => {
                    const v = amountsMap?.[header];
                    return (
                        <td
                            key={`${header}-${j}`}
                            className={clsx(styles.TableBodyRowItem, styles.AggregateCell, { [styles.Negative]: v != null && v < 0 })}
                        >
                            {v != null ? formatTableAmount(v / unitDivisor) : '-'}
                        </td>
                    );
                })}
                <td className={clsx(styles.TableBodyRowItem, styles.AggregateCell, { [styles.Negative]: aggTotal < 0 })}>
                    {formatTableAmount(aggTotal / unitDivisor)}
                </td>
            </tr>
        );
    };

    const renderEntitySubRow = (subRow: CashflowSubRow, groupKey: string, groupLabel: string) => {
        const isOther = subRow.bucketType === 'other';
        const isEntity = subRow.bucketType === 'entity';

        // displayName: raw string used for keys and drawer title; formattedName: title-cased for render only.
        const displayName = isOther ? `Other ${groupLabel}` : subRow.name ?? '';
        const formattedName = isOther ? displayName : toDisplayName(displayName);

        // Chip ladder: tier-1 by entityId → source → fallback name pill; tier-2 by entityId; tier-3 none.
        let chipInfo: ChipInfo | null = null;
        if (!isOther) {
            if (subRow.entityId != null) {
                chipInfo = entityChipMap.get(subRow.entityId) ?? null;
            }
            if (chipInfo == null && !isEntity) {
                chipInfo = getSourceChipInfo(subRow.source);
            }
        }
        // Fallback rung: no structured chip → grey pill from served rationale.
        // kind-1 (rationale): use bucketKey (= full raw rationale string), skip payment-method noise.
        // kind-2 (entity, chipInfo==null): look up served rationale via entityRationaleLabel.
        // kind-3 (other) and rows with a structured chip: excluded.
        const fallbackLabel = (() => {
            if (isOther || chipInfo != null) return null;
            if (!isEntity) return subRow.bucketKey && !isNoiseRationale(subRow.bucketKey) ? toDisplayName(subRow.bucketKey) : null;
            // kind-2: entityChipMap returned null — use served rationale scoped to this row's classificationId.
            // Disabled when classificationId is absent from response items (pre-contract backend).
            if (!kind2RationaleReady || subRow.entityId == null) return null;
            const key = `${subRow.entityId}:${subRow.classificationId ?? null}`;
            const served = entityRationaleLabel.get(key);
            return served ? toDisplayName(served) : null;
        })();

        // Drawer payload per bucket type.
        const drawerPayload: BucketPayload = {
            bucketType: subRow.bucketType,
            bucketKey: subRow.bucketKey ?? null,
            classificationId: subRow.classificationId,
            ...(effectiveWindow ? effectiveWindow : {}),
        };

        return (
            <tr key={`${groupKey}-${subRow.name}-${subRow.bucketType}`} className={clsx(styles.TableBodyRow, styles.SubRow)}>
                <td className={styles.TableBodyRowItem}>
                    <span className={styles.NameCell}>
                        <span className={clsx({ [styles.ResidualName]: isOther })} title={formattedName || '-'}>
                            {formattedName || '-'}
                        </span>
                        <span
                            className={styles.ViewTxn}
                            role="button"
                            tabIndex={0}
                            onClick={() => openDrawer(formattedName, drawerPayload)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    openDrawer(formattedName, drawerPayload);
                                }
                            }}
                        >
                            View Transactions
                            <svg className={styles.ViewTxnArrow} viewBox="0 0 12 12" width="12" height="12" aria-hidden="true">
                                <circle cx="6" cy="6" r="6" fill="currentColor" />
                                <path
                                    d="M4.3 7.7 7.7 4.3M4.9 4.3h2.8v2.8"
                                    stroke="white"
                                    strokeWidth="1.2"
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                    </span>
                </td>
                <td className={styles.TableBodyRowItem}>
                    {renderChips(chipInfo)}
                    {fallbackLabel && (
                        <span className={styles.Chip} title={fallbackLabel}>
                            <span className={styles.ChipLabel}>{fallbackLabel}</span>
                        </span>
                    )}
                </td>
                {periodCells(subRow.amountsMap)}
                {totalCell(subRow.amountsMap)}
            </tr>
        );
    };

    const renderGroupRow = (groupRow: GroupRow, inSection = false) => {
        const { row, count, label, kind } = groupRow;
        const groupKey = `${kind}:${label}`;
        const hasSubRows = count > 0;
        const expanded = hasSubRows && expandedGroups.has(groupKey);
        const toggle = () => hasSubRows && toggleGroup(groupKey);

        let leafRows: JSX.Element[] = [];
        if (expanded) {
            const sorted = sortedSubRows(row.subRows ?? []) as CashflowSubRow[];
            const nonOther = sorted.filter((s) => s.bucketType !== 'other');
            const other = sorted.filter((s) => s.bucketType === 'other');
            const visibleCount = visibleCountByGroup.get(groupKey) ?? LEAF_PAGE_SIZE;
            const visibleLeaves = nonOther.slice(0, visibleCount);
            const remaining = nonOther.length - visibleCount;
            const loadMore = () => setVisibleCountByGroup((prev) => new Map(prev).set(groupKey, visibleCount + LEAF_PAGE_SIZE));
            leafRows = [
                ...visibleLeaves.map((s) => renderEntitySubRow(s, groupKey, label)),
                ...(remaining > 0
                    ? [
                          <tr key={`${groupKey}-load-more`}>
                              <td className={styles.TableBodyRowItem} colSpan={totalColumnCount}>
                                  <span
                                      className={styles.LoadMore}
                                      role="button"
                                      tabIndex={0}
                                      onClick={loadMore}
                                      onKeyDown={(e) => {
                                          if (e.key === 'Enter' || e.key === ' ') {
                                              e.preventDefault();
                                              loadMore();
                                          }
                                      }}
                                  >
                                      <img src={caretRightPurple} alt="" className={styles.LoadMoreChevron} />
                                      {`See more (${remaining} Transactions)`}
                                  </span>
                              </td>
                          </tr>,
                      ]
                    : []),
                ...other.map((s) => renderEntitySubRow(s, groupKey, label)),
            ];
        }

        return (
            <Fragment key={groupKey}>
                <tr className={inSection ? clsx(styles.GroupRow, styles.SectionGroupRow) : styles.GroupRow}>
                    <td className={styles.TableBodyRowItem}>
                        <span
                            className={clsx(styles.GroupNameCell, { [styles.GroupNameDisabled]: !hasSubRows })}
                            role="button"
                            tabIndex={hasSubRows ? 0 : -1}
                            onClick={toggle}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    toggle();
                                }
                            }}
                        >
                            <img
                                src={dropdownBlack}
                                alt=""
                                width="10px"
                                className={styles.Chevron}
                                style={{ transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)' }}
                            />
                            <span className={clsx(SPECIAL_LABEL_CLASS[kind])}>{label}</span>
                            <span className={styles.CountBadge}>{count}</span>
                        </span>
                    </td>
                    <td className={styles.TableBodyRowItem} />
                    {periodCells(row.amountsMap)}
                    {totalCell(row.amountsMap)}
                </tr>
                {leafRows}
            </Fragment>
        );
    };

    return (
        <div className={styles.FinancialsFromBankingV2}>
            <div className={styles.TableContainer}>
                {fetchingData ? (
                    <LoadingSpinner height="60px" style={{ marginTop: '100px' }} color="black" />
                ) : noData ? (
                    <EmptyBank text="Data Not Available" />
                ) : (
                    // Multi-table layout: one <table> per section.
                    // Sticky <td> is constrained to its own <table> box in Chrome (not tbody).
                    // Stacked tables in the same scroll container make each section's band
                    // (top:2.25rem) and group rows (top:4.5rem) un-pin when that table scrolls out.
                    // Column sync: table-layout:fixed + shared <col> widths via renderColGroup().
                    // Assumption to verify on live probe: sticky offset is relative to the scroll
                    // container (TableContainer), bounded per table.
                    <>
                        {/* Sticky header: the <div> sticks (not the table element).
                            A sticky <table> in Chrome creates a stacking context that extends
                            over body content and swallows pointer events on group rows below.
                            A sticky <div> is sized to its content (one header row) so it never
                            extends beyond its own height — no click-intercept issue. */}
                        <div className={styles.StickyHeader}>
                            <table className={styles.Table}>
                                {renderColGroup()}
                                <thead className={styles.TableHead}>
                                    <tr className={styles.TableHeadRow}>
                                        <th className={styles.TableHeadRowItem} />
                                        <th className={clsx(styles.TableHeadRowItem, styles.RationaleHeader)}>Tagging Rational</th>
                                        {periodHeaders.map((header: string) => (
                                            <th key={header} className={styles.TableHeadRowItem}>
                                                {header.trim().replace(' ', "'")}
                                            </th>
                                        ))}
                                        <th className={clsx(styles.TableHeadRowItem, styles.TotalHeader)}>Total</th>
                                    </tr>
                                </thead>
                            </table>
                        </div>

                        <table className={styles.Table}>
                            {renderColGroup()}
                            <tbody className={styles.TableBody}>{renderAggregateRow('Opening Balance', aggregates.opening, false, false, 'first')}</tbody>
                        </table>

                        {layout.sections.map((section) => (
                            <table key={section.band} className={styles.Table}>
                                {renderColGroup()}
                                <tbody className={styles.TableBody}>
                                    {renderSectionBand(section.band)}
                                    {section.groupRows.map((groupRow) => renderGroupRow(groupRow, true))}
                                    {renderNetRow(section.netLabel, section.netAmountsMap)}
                                </tbody>
                            </table>
                        ))}

                        {layout.specialRows.map((special) => (
                            <table key={`special-${special.kind}`} className={styles.Table}>
                                {renderColGroup()}
                                <tbody className={styles.TableBody}>{renderGroupRow(special)}</tbody>
                            </table>
                        ))}

                        <table className={styles.Table}>
                            {renderColGroup()}
                            <tbody className={styles.TableBody}>
                                {renderAggregateRow('Net Cashflow', aggregates.netCashflow, true, true)}
                                {renderAggregateRow('Closing Balance', aggregates.closing, false, false, 'last')}
                            </tbody>
                        </table>
                    </>
                )}
            </div>

            {drawer && (
                <EntityDrillDownDrawer
                    entityName={drawer.entityName}
                    transactions={drawer.transactions}
                    loading={drawer.loading}
                    onClose={() => setDrawer(null)}
                />
            )}
        </div>
    );
}

const FORMULA_HOVER = (
    <div className={styles.FormulaContainer}>
        <div className={styles.Label}>N</div>=<div className={styles.Label}>C</div>-<div className={styles.Label}>B</div>
    </div>
);

export default FinancialsFromBankingV2;
